import type { ReactNode } from "react";
import { css } from "styled-system/css";

export interface DataTable {
  columns: string[];
  rows: Array<Array<string | number>>;
}

interface FigureProps {
  /** States the finding, not the chart type. */
  title: string;
  sub?: ReactNode;
  /** One line on what was counted and how. */
  method: ReactNode;
  table?: DataTable;
  children: ReactNode;
}

/**
 * A titled figure with a method note and an optional "show the data table"
 * disclosure, after the reference's .fig grammar.
 */
export function Figure({ title, sub, method, table, children }: FigureProps) {
  return (
    <figure className={styles.fig}>
      <h3 className={styles.title}>{title}</h3>
      {sub && <p className={styles.sub}>{sub}</p>}
      <div className={styles.body}>{children}</div>
      {table && table.rows.length > 0 && (
        <details className={styles.details}>
          <summary>Show the data table</summary>
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  {table.columns.map((c, i) => (
                    <th key={c} data-num={i > 0 || undefined}>
                      {c}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {table.rows.map((r, i) => (
                  <tr key={i}>
                    {r.map((v, j) => (
                      <td key={j} data-num={j > 0 || undefined}>
                        {v}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}
      <figcaption className={styles.method}>{method}</figcaption>
    </figure>
  );
}

const styles = {
  fig: css({ m: 0, minWidth: 0 }),
  title: css({
    fontFamily: "sans",
    fontSize: "16px",
    fontWeight: 700,
    lineHeight: 1.3,
    color: "ed.ink",
    m: 0,
    mb: "2px",
  }),
  sub: css({
    fontFamily: "sans",
    fontSize: "13px",
    color: "ed.ink2",
    lineHeight: 1.4,
    m: 0,
    mb: "10px",
  }),
  body: css({ "& svg": { display: "block", width: "100%", height: "auto" } }),
  details: css({
    mt: "6px",
    fontFamily: "sans",
    fontSize: "12px",
    color: "ed.ink2",
    "& summary": {
      cursor: "pointer",
      width: "fit-content",
      textDecoration: "underline",
      textUnderlineOffset: "2px",
      textDecorationColor: "ed.rule",
    },
  }),
  tableWrap: css({ overflowX: "auto", mt: "6px" }),
  table: css({
    borderCollapse: "collapse",
    fontSize: "12px",
    "& th": {
      textAlign: "left",
      fontFamily: "sans",
      fontWeight: 600,
      fontSize: "10.5px",
      letterSpacing: "0.06em",
      textTransform: "uppercase",
      color: "ed.ink3",
      borderBottomWidth: "1px",
      borderBottomStyle: "solid",
      borderColor: "ed.ink",
      py: "3px",
      pr: "14px",
    },
    "& td": {
      borderBottomWidth: "1px",
      borderBottomStyle: "solid",
      borderColor: "ed.rule",
      py: "3px",
      pr: "14px",
      color: "ed.ink",
    },
    "& [data-num]": {
      textAlign: "right",
      fontFamily: "mono",
      fontVariantNumeric: "tabular-nums",
    },
  }),
  method: css({
    mt: "8px",
    fontFamily: "mono",
    fontSize: "10.5px",
    lineHeight: 1.5,
    color: "ed.ink3",
    fontVariantNumeric: "tabular-nums",
  }),
};
